import { useState, useEffect, useCallback } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useReverseGeocode } from "@/hooks/use-reverse-geocode";
import { useUsername } from "@/hooks/use-username";
import { UsernamePrompt } from "./username-prompt";
import { Plus, X, Loader2, MapPin } from "lucide-react";

interface CodeFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  editingCodeId?: Id<"accessCodes"> | null;
}

export function CodeFormSheet({
  open,
  onOpenChange,
  onSuccess,
  editingCodeId,
}: CodeFormSheetProps) {
  const { toast } = useToast();
  const { reverseGeocode, isLoading: isGeocoding } = useReverseGeocode();
  const { hasUsername } = useUsername();

  // Get fresh GPS position (bypasses cached shift tracking position)
  const getFreshPosition = useCallback((): Promise<{ lat: number; lng: number } | null> => {
    return new Promise((resolve) => {
      if (!("geolocation" in navigator)) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0, // Force fresh reading, no cache
        }
      );
    });
  }, []);

  const [showUsernamePrompt, setShowUsernamePrompt] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Form state
  const [community, setCommunity] = useState("");
  const [street, setStreet] = useState("");
  const [codes, setCodes] = useState<string[]>([""]);
  const [location, setLocation] = useState<{
    lat: number;
    lng: number;
    city: string;
  } | null>(null);

  const isEditing = !!editingCodeId;
  const editingCode = useQuery(
    api.accessCodes.get,
    editingCodeId ? { id: editingCodeId } : "skip"
  );

  const createCode = useMutation(api.accessCodes.create);
  const updateCode = useMutation(api.accessCodes.update);

  // Check username when sheet opens (only for new codes)
  useEffect(() => {
    if (open && !hasUsername && !isEditing) {
      setShowUsernamePrompt(true);
    }
  }, [open, hasUsername, isEditing]);

  // Get location when sheet opens (only for new codes)
  useEffect(() => {
    if (open && hasUsername && !location && !isEditing) {
      getLocation();
    }
  }, [open, hasUsername, isEditing]);

  // Populate form when editing
  useEffect(() => {
    if (isEditing && editingCode && open) {
      setCommunity(editingCode.community);
      setStreet(editingCode.street);
      setCodes(editingCode.codes);
      setLocation({
        lat: editingCode.lat,
        lng: editingCode.lng,
        city: editingCode.city,
      });
    }
  }, [isEditing, editingCode, open]);

  const getLocation = async () => {
    setIsGettingLocation(true);
    try {
      const pos = await getFreshPosition();
      if (pos) {
        const geo = await reverseGeocode(pos.lat, pos.lng);
        setLocation({
          lat: pos.lat,
          lng: pos.lng,
          city: geo?.city ?? "Unknown",
        });
      } else {
        toast({
          title: "Location error",
          description: "Could not get your location. Please enable location services.",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Location error",
        description: "Could not get your location",
        variant: "destructive",
      });
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleAddCode = () => {
    setCodes([...codes, ""]);
  };

  const handleRemoveCode = (index: number) => {
    if (codes.length > 1) {
      setCodes(codes.filter((_, i) => i !== index));
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    const newCodes = [...codes];
    newCodes[index] = value;
    setCodes(newCodes);
  };

  const resetForm = () => {
    setCommunity("");
    setStreet("");
    setCodes([""]);
    setLocation(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!location) {
      toast({
        title: "Location required",
        description: "Please wait for location to be captured",
        variant: "destructive",
      });
      return;
    }

    const validCodes = codes.map((c) => c.trim()).filter(Boolean);
    if (validCodes.length === 0) {
      toast({
        title: "Code required",
        description: "Please enter at least one access code",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditing && editingCodeId) {
        await updateCode({
          id: editingCodeId,
          community: community.trim(),
          street: street.trim(),
          codes: validCodes,
        });
        toast({
          title: "Code updated",
          description: "Access code has been updated",
        });
      } else {
        await createCode({
          community: community.trim(),
          city: location.city,
          street: street.trim(),
          codes: validCodes,
          lat: location.lat,
          lng: location.lng,
        });
        toast({
          title: "Code added",
          description: "Access code added to community database",
        });
      }

      resetForm();
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to add code",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid =
    community.trim() &&
    street.trim() &&
    codes.some((c) => c.trim()) &&
    location;

  return (
    <>
      <UsernamePrompt
        open={showUsernamePrompt}
        onComplete={() => setShowUsernamePrompt(false)}
      />

      <Sheet open={open && !showUsernamePrompt} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[85vh] flex flex-col">
          <SheetHeader>
            <SheetTitle>{isEditing ? "Edit Access Code" : "Add Access Code"}</SheetTitle>
          </SheetHeader>

          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto space-y-6 mt-6"
          >
            {/* Location indicator */}
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              {isGettingLocation || isGeocoding ? (
                <span className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Getting location...
                </span>
              ) : location ? (
                <span className="text-sm">{location.city}</span>
              ) : (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={getLocation}
                  className="h-auto p-0"
                >
                  Tap to get location
                </Button>
              )}
            </div>

            {/* Community name */}
            <div className="space-y-2">
              <Label htmlFor="community">Community Name *</Label>
              <Input
                id="community"
                value={community}
                onChange={(e) => setCommunity(e.target.value)}
                placeholder="Rolling Hills, Pine Ridge HOA..."
                required
              />
            </div>

            {/* Street/Gate */}
            <div className="space-y-2">
              <Label htmlFor="street">Gate Location *</Label>
              <Input
                id="street"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                placeholder="Oak Ave Gate, Main Entrance..."
                required
              />
            </div>

            {/* Codes */}
            <div className="space-y-2">
              <Label>Access Code(s) *</Label>
              {codes.map((code, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={code}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    placeholder="#1234, *5678..."
                    className="font-mono"
                  />
                  {codes.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveCode(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddCode}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add another code
              </Button>
            </div>
          </form>

          {/* Sticky buttons - extra padding for bottom nav bar */}
          <div className="sticky bottom-0 bg-background border-t -mx-6 px-6 py-3 pb-[calc(env(safe-area-inset-bottom)+4rem)] mt-auto">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                className="flex-1 h-12"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!isValid || isSubmitting}
                className="flex-1 h-12"
              >
                {isSubmitting
                  ? isEditing ? "Saving..." : "Adding..."
                  : isEditing ? "Save Changes" : "Add Code"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
